// Fiche d'une source : droits minimaux attendus, état, historique des lectures (paginé), erreurs lisibles,
// contradictions ouvertes, journal, test de connexion et désactivation (jamais de suppression).
// Aucun secret : la référence du coffre n'est montrée qu'à l'administration (l'API ne la rend qu'à elle), et jamais
// sa valeur.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ErreurApi, nouvelleCle } from '../api/client';
import type { ErreurVue, EssaiVue, LectureVue, SourceDetail } from '../api/types';
import { Attendre, Chargement, Erreur, Etiquette, EtiquetteEtatSource } from '../composants/communs';
import { useCarte, useEkera } from '../contexte';
import { indexer, nomDe } from '../domaine/carte';
import { dateFr, idCourt } from '../domaine/format';
import {
  dureeHeures,
  dureeMs,
  ESSAI_TERMINE,
  EXPLICATION_ETAT_SOURCE,
  LIBELLE_ACTION_JOURNAL,
  LIBELLE_ETAT_ESSAI,
} from '../domaine/sources';
import { lienVers } from '../routeur';
import { RecetteExportVue } from './SourceAjout';

/** Réglage de l'interrogation d'un test (modifiable par les tests). */
export const reglagesTest = { intervalleMs: 2000 };

export function FicheSource({ sid }: { sid: string }) {
  const { client, cid } = useEkera();
  const source = useQuery({ queryKey: ['carte', cid, 'sources', sid], queryFn: () => client.source(cid, sid) });
  return (
    <div className="pile">
      <p>
        <a href={lienVers('sources')}>← Toutes les sources</a>
      </p>
      <Attendre etats={[source]} quoi="de la source">
        {() => (source.data ? <Contenu s={source.data} /> : null)}
      </Attendre>
    </div>
  );
}

function Contenu({ s }: { s: SourceDetail }) {
  const { administrateur } = useEkera();
  return (
    <>
      <section className="panneau" aria-labelledby="t-fiche">
        <div className="panneau-tete">
          <h2 id="t-fiche">
            {s.nom} <EtiquetteEtatSource etat={s.etat} />
          </h2>
          <span className="indice">{s.mode === 'connecteur' ? 'Lue par un connecteur' : 'Export déposé par vos équipes'}</span>
        </div>
        <p className="note">{EXPLICATION_ETAT_SOURCE[s.etat]}</p>
        <dl className="paires">
          <dt>Logiciel</dt>
          <dd>
            {s.nom} <span className="num">({s.logiciel})</span>
          </dd>
          {s.mode === 'connecteur' && (
            <>
              <dt>Adresse</dt>
              <dd>{s.adresse ?? '—'}</dd>
              <dt>Compte de lecture attendu</dt>
              <dd>{s.compte ?? '—'}</dd>
              <dt>Référence du secret</dt>
              <dd>
                {administrateur && s.reference_secret ? (
                  <>
                    <span className="num">{s.reference_secret}</span>{' '}
                    <span className="note">(référence dans votre coffre ; Ekera n’en connaît pas la valeur)</span>
                  </>
                ) : (
                  <span className="note">Réservée au rôle « administration » ; Ekera ne détient jamais la valeur du secret.</span>
                )}
              </dd>
            </>
          )}
          <dt>Collecteur</dt>
          <dd data-testid="collecteur-source">
            {s.collecteur ? (
              (s.collecteur.nom ?? s.collecteur.id)
            ) : (
              <span className="note">Aucun collecteur : la source n’est lue par personne</span>
            )}
          </dd>
          <dt>Lecture attendue</dt>
          <dd>
            toutes les {dureeHeures(s.frequence_lecture_h)} ; « en retard » au-delà de {dureeHeures(s.retard_apres_h)}
          </dd>
          <dt>Dernière lecture</dt>
          <dd>
            {s.derniere_lecture ? `${dateFr(s.derniere_lecture.le)} (${s.derniere_lecture.statut === 'ok' ? 'réussie' : 'en échec'})` : 'Aucune lecture'}
          </dd>
          <dt>Dernière lecture réussie</dt>
          <dd>{dateFr(s.derniere_lecture_reussie_le)}</dd>
          <dt>Objets apportés</dt>
          <dd>{s.objets ?? '—'} (dernière lecture réussie)</dd>
          <dt>Lectures déposées</dt>
          <dd>{s.lectures}</dd>
          <dt>Déclarée le</dt>
          <dd>{dateFr(s.cree_le)}</dd>
        </dl>
      </section>

      {s.derniere_erreur && (
        <section className="panneau" aria-labelledby="t-erreur">
          <h2 id="t-erreur">Pourquoi la dernière lecture a échoué</h2>
          <BlocErreur e={s.derniere_erreur} />
        </section>
      )}

      <Droits s={s} />

      {s.mode === 'connecteur' ? <Test s={s} /> : null}

      <Contradictions s={s} />
      <Historique s={s} />
      <JournalSource s={s} />
      {administrateur && <Activation s={s} />}
    </>
  );
}

export function BlocErreur({ e }: { e: ErreurVue }) {
  return (
    <div className="encadre erreur" role="group" aria-label={`Erreur : ${e.message}`}>
      <p>
        <b>{e.message}</b>
      </p>
      <p>
        <b>Que faire :</b> {e.conseil}
      </p>
      {e.detail && (
        <details>
          <summary>Précision du collecteur</summary>
          <p className="note">{e.detail}</p>
        </details>
      )}
    </div>
  );
}

function Droits({ s }: { s: SourceDetail }) {
  const cat = s.catalogue as { lecture?: { methode?: string; droits_minimaux?: string }; apporte?: string; recette_export?: unknown } | null;
  if (!cat) return null;
  return (
    <section className="panneau" aria-labelledby="t-droits">
      <h2 id="t-droits">Droits minimaux attendus</h2>
      <dl className="paires">
        <dt>Droits du compte de lecture</dt>
        <dd>{cat.lecture?.droits_minimaux ?? '—'}</dd>
        <dt>Méthode de lecture</dt>
        <dd>{cat.lecture?.methode ?? '—'}</dd>
        {cat.apporte && (
          <>
            <dt>Ce que la lecture apporte</dt>
            <dd>{cat.apporte}</dd>
          </>
        )}
      </dl>
      <p className="note">
        Donnez au compte de lecture ces droits et pas davantage : un compte en lecture seule, distinct de tout compte
        d’écriture.
      </p>
      {s.mode === 'export' && <RecetteExportVue recette={(cat as { recette_export: never }).recette_export} />}
    </section>
  );
}

// ------------------------------------------------------------------ test de connexion

function Test({ s }: { s: SourceDetail }) {
  const { client, cid, ecritureAutorisee, motifLectureSeule } = useEkera();
  const qc = useQueryClient();
  const cle = useRef<string | null>(null);
  const [tid, setTid] = useState<string | null>(null);
  const dernier = s.tests[0] as EssaiVue | undefined;
  const enAttente = dernier && !ESSAI_TERMINE(dernier.etat) ? dernier.id : null;
  const suivi = tid ?? enAttente;

  const demande = useMutation({
    mutationFn: () => {
      // même clé pour un nouvel essai après une coupure ; nouvelle clé une fois la demande acceptée
      cle.current ??= nouvelleCle();
      return client.demanderTest(cid, s.id, cle.current);
    },
    onSuccess: (e) => {
      cle.current = null;
      setTid(e.id);
    },
    onError: (e) => {
      if (e instanceof ErreurApi && e.statut !== 0) cle.current = null;
    },
  });

  const essai = useQuery({
    queryKey: ['carte', cid, 'sources', s.id, 'test', suivi],
    queryFn: () => client.essaiSource(cid, s.id, suivi as string),
    enabled: !!suivi,
    refetchInterval: (q) => (q.state.data && ESSAI_TERMINE(q.state.data.etat) ? false : reglagesTest.intervalleMs),
  });
  const fini = essai.data && ESSAI_TERMINE(essai.data.etat);
  useEffect(() => {
    // le test est terminé : la fiche (état, tests) est relue
    if (fini) void qc.invalidateQueries({ queryKey: ['carte', cid, 'sources', s.id], exact: true });
  }, [fini, qc, cid, s.id]);

  const peut = ecritureAutorisee && s.actif;
  const montre = essai.data ?? (!suivi ? dernier : undefined);
  return (
    <section className="panneau" aria-labelledby="t-test">
      <h2 id="t-test">Tester la connexion</h2>
      <p className="note">
        Le test est exécuté par le collecteur sur site, avec le secret lu dans <b>votre</b> coffre. Ekera ne se connecte à rien et
        ne reçoit aucune valeur sensible : seulement le résultat.
      </p>
      <div className="ligne">
        <button type="button" className="btn principal" disabled={!peut || demande.isPending || (!!suivi && !fini && !essai.error)} onClick={() => demande.mutate()}>
          Tester
        </button>
        {!s.actif && <span className="note">Source désactivée : réactivez-la avant de la tester.</span>}
        {s.actif && !ecritureAutorisee && <span className="note">{motifLectureSeule}</span>}
      </div>
      {demande.error && <Erreur erreur={demande.error} titre="Le test n’a pas pu être demandé." />}
      {essai.error && <Erreur erreur={essai.error} titre="Le suivi du test a échoué." />}
      {suivi && essai.isPending && <Chargement quoi="du test" />}
      {montre && <ResultatEssai e={montre} dernierConnu={!suivi} />}
    </section>
  );
}

export function ResultatEssai({ e, dernierConnu = false }: { e: EssaiVue; dernierConnu?: boolean }) {
  const r = e.resultat;
  return (
    <div className="pile" role="status" aria-live="polite" data-testid="resultat-test" style={{ gap: 8 }}>
      <p>
        <b>{dernierConnu ? 'Dernier test' : 'Test'} :</b> {LIBELLE_ETAT_ESSAI[e.etat]}
        <span className="note"> · demandé le {dateFr(e.demande_le)}{e.demande_par ? ` par ${e.demande_par}` : ''}</span>
      </p>
      {e.etat === 'expiree' && (
        <div className="encadre attention">
          <p>Le collecteur n’a pas répondu. Vérifiez qu’il est démarré chez vous et qu’il joint Ekera, puis relancez le test.</p>
        </div>
      )}
      {r && (
        <>
          <p>
            <Etiquette ton={r.reussi ? 'ok' : 'grave'}>{r.reussi ? 'Connexion réussie' : 'Connexion en échec'}</Etiquette>
          </p>
          <dl className="paires">
            <dt>Durée</dt>
            <dd>{dureeMs(r.duree_ms)}</dd>
            {r.compte_constate && (
              <>
                <dt>Compte constaté</dt>
                <dd>
                  {r.compte_constate}{' '}
                  {r.compte_conforme === true && <Etiquette ton="ok">conforme au compte déclaré</Etiquette>}
                  {r.compte_conforme === false && <Etiquette ton="grave">différent du compte déclaré</Etiquette>}
                </dd>
              </>
            )}
            {r.version_logiciel && (
              <>
                <dt>Version du logiciel</dt>
                <dd>{r.version_logiciel}</dd>
              </>
            )}
            <dt>Droits manquants</dt>
            <dd>
              {r.droits_manquants.length === 0 ? (
                'Aucun'
              ) : (
                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                  {r.droits_manquants.map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              )}
            </dd>
          </dl>
          {r.erreurs.map((x, i) => (
            <BlocErreur key={`${x.code}-${i}`} e={x} />
          ))}
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ contradictions, historique, journal

function Contradictions({ s }: { s: SourceDetail }) {
  // Nom lisible de l'objet (jamais son identifiant technique) ; tant que la carte n'est pas lue, repli sur le nom court de l'identifiant.
  const carte = useCarte();
  const ix = useMemo(() => (carte.data ? indexer(carte.data) : null), [carte.data]);
  const nomObjet = (id: string) => (ix ? nomDe(ix, id) : idCourt(id));
  return (
    <section className="panneau" aria-labelledby="t-contra">
      <h2 id="t-contra">Points à confirmer : contradictions ouvertes ({s.contradictions_ouvertes})</h2>
      {s.contradictions.length === 0 ? (
        <p className="note">Aucun point à confirmer ne cite cette source.</p>
      ) : (
        <ul className="liste-points">
          {s.contradictions.map((c) => (
            <li key={c.id}>
              {c.sujet}
              <small>
                {c.objet ? (
                  <>
                    Objet : <a href={lienVers('carte', { objet: c.objet })}>{nomObjet(c.objet)}</a>
                  </>
                ) : (
                  'Sans objet précis'
                )}
                {c.champ ? ` · champ ${c.champ}` : ''}
              </small>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Historique({ s }: { s: SourceDetail }) {
  const { client, cid } = useEkera();
  const [plus, setPlus] = useState<LectureVue[]>([]);
  const [suivante, setSuivante] = useState<number | null>(s.lectures_suivantes ?? null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [charge, setCharge] = useState(false);
  // la fiche a été relue (test terminé, désactivation) : on repart de la première page
  useEffect(() => {
    setPlus([]);
    setSuivante(s.lectures_suivantes ?? null);
  }, [s]);
  async function suite() {
    if (suivante === null) return;
    setErreur(null);
    setCharge(true);
    try {
      const p = await client.source(cid, s.id, { avant: suivante });
      setPlus((x) => [...x, ...p.historique_lectures]);
      setSuivante(p.lectures_suivantes ?? null);
    } catch (e) {
      setErreur(e);
    } finally {
      setCharge(false);
    }
  }
  const lectures = [...s.historique_lectures, ...plus];
  return (
    <section className="panneau" aria-labelledby="t-histo">
      <h2 id="t-histo">Historique des lectures</h2>
      {lectures.length === 0 ? (
        <p className="note">Aucune lecture déposée pour l’instant.</p>
      ) : (
        <div className="tableau-defile">
          <table>
            <caption className="sr">Lectures de cette source, de la plus récente à la plus ancienne</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Résultat</th>
                <th scope="col" className="num">
                  Objets
                </th>
                <th scope="col" className="num">
                  Liens
                </th>
                <th scope="col" className="num">
                  Contradictions
                </th>
                <th scope="col" className="num">
                  Durée
                </th>
              </tr>
            </thead>
            <tbody>
              {lectures.map((l) => (
                <LigneLecture key={l.id} l={l} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {erreur !== null && <Erreur erreur={erreur} titre="Les lectures plus anciennes n’ont pas pu être lues." />}
      {suivante !== null && (
        <p>
          <button type="button" className="btn petit" onClick={() => void suite()} disabled={charge}>
            Voir les lectures plus anciennes
          </button>
        </p>
      )}
    </section>
  );
}

function LigneLecture({ l }: { l: LectureVue }) {
  return (
    <>
      <tr>
        <td>{dateFr(l.le)}</td>
        <td>
          <Etiquette ton={l.statut === 'ok' ? 'ok' : 'grave'}>{l.statut === 'ok' ? 'Réussie' : 'En échec'}</Etiquette>
          {l.suspecte && (
            <>
              {' '}
              <Etiquette ton="grave">Suspecte</Etiquette>
              <span className="note" style={{ display: 'block' }}>
                Déposée par un collecteur révoqué pour compromission : ignorée dans l’état de la source, jamais effacée.
              </span>
            </>
          )}
          {l.collecteur && <span className="note" style={{ display: 'block' }}>Lue par {l.collecteur.nom ?? l.collecteur.id}</span>}
        </td>
        <td className="num">{l.objets}</td>
        <td className="num">{l.liens}</td>
        <td className="num">{l.contradictions}</td>
        <td className="num">{dureeMs(l.duree_ms)}</td>
      </tr>
      {l.erreurs.length > 0 && (
        <tr>
          <td colSpan={6}>
            <div className="pile" style={{ gap: 8 }}>
              {l.erreurs.map((e, i) => (
                <BlocErreur key={`${e.code}-${i}`} e={e} />
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function JournalSource({ s }: { s: SourceDetail }) {
  return (
    <section className="panneau" aria-labelledby="t-journal">
      <h2 id="t-journal">Journal de la source</h2>
      <ul className="liste-points">
        {s.journal.map((j, i) => (
          <li key={`${j.le}-${i}`} className="ok">
            <b>{LIBELLE_ACTION_JOURNAL[j.action] ?? j.action}</b> le {dateFr(j.le)}
            {j.par ? ` par ${j.par}` : ''}
            {j.motif && <small>Motif : {j.motif}</small>}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ------------------------------------------------------------------ désactivation / réactivation (administration)

type EtapeActivation = 'saisie' | 'confirmation';

function Activation({ s }: { s: SourceDetail }) {
  const { client, cid, ecritureAutorisee } = useEkera();
  const qc = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [etape, setEtape] = useState<EtapeActivation>('saisie');
  const [motif, setMotif] = useState('');
  const idMotif = useId();
  const action = s.actif ? 'desactiver' : 'reactiver';
  const m = useMutation({
    mutationFn: () => (s.actif ? client.desactiverSource(cid, s.id, motif.trim()) : client.reactiverSource(cid, s.id, motif.trim())),
    onSuccess: async () => {
      setOuvert(false);
      setEtape('saisie');
      setMotif('');
      await qc.invalidateQueries({ queryKey: ['carte', cid, 'sources'] });
    },
  });
  const motifOk = motif.trim().length >= 1 && motif.length <= 256;
  return (
    <section className="panneau" aria-labelledby="t-activation">
      <h2 id="t-activation">{s.actif ? 'Arrêter la lecture' : 'Reprendre la lecture'}</h2>
      {!ouvert ? (
        <>
          <p className="note">
            {s.actif
              ? 'Une source n’est jamais supprimée : on la désactive. Elle n’est plus lue, mais son historique, les objets déjà apportés et ses contradictions restent. Une réactivation est possible à tout moment.'
              : 'Cette source est désactivée. La réactiver reprend sa lecture ; rien de l’historique n’a été perdu.'}
          </p>
          <p>
            <button type="button" className="btn" disabled={!ecritureAutorisee} onClick={() => setOuvert(true)}>
              {s.actif ? 'Désactiver cette source…' : 'Réactiver cette source…'}
            </button>
          </p>
        </>
      ) : etape === 'saisie' ? (
        <form
          className="pile"
          onSubmit={(e) => {
            e.preventDefault();
            if (motifOk) setEtape('confirmation');
          }}
        >
          <div className="champ">
            <label htmlFor={idMotif}>
              Motif <span className="aide">— obligatoire, gardé dans le journal de la source (256 caractères au plus)</span>
            </label>
            <textarea id={idMotif} value={motif} maxLength={256} onChange={(e) => setMotif(e.target.value)} required />
          </div>
          <div className="ligne">
            <button type="submit" className="btn principal" disabled={!motifOk}>
              Simuler
            </button>
            <button type="button" className="btn" onClick={() => setOuvert(false)}>
              Annuler
            </button>
          </div>
        </form>
      ) : (
        <div className="pile">
          <div className="encadre info" role="region" aria-label="Simulation">
            <p>
              <b>Ce qui va se passer.</b>{' '}
              {action === 'desactiver'
                ? `« ${s.nom} » ne sera plus lue par le collecteur. Rien n’est supprimé : historique, objets apportés et contradictions restent visibles.`
                : `« ${s.nom} » sera de nouveau lue par le collecteur, à sa prochaine échéance.`}
            </p>
            <p>
              Motif noté au journal : <q>{motif.trim()}</q>
            </p>
          </div>
          {m.error && <Erreur erreur={m.error} titre="L’API a refusé le changement." />}
          <div className="ligne">
            <button type="button" className="btn principal" disabled={m.isPending} onClick={() => m.mutate()}>
              {action === 'desactiver' ? 'Confirmer la désactivation' : 'Confirmer la réactivation'}
            </button>
            <button type="button" className="btn" onClick={() => setEtape('saisie')} disabled={m.isPending}>
              Modifier le motif
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
